import React from 'react';
import { DateTime } from 'luxon';
import { useIsMobileDimension } from '@beef/ui-kit';

import { AccumServiceType, EnrichedAccumItemType } from 'types/accumulators';
import {
  formatTimeUnit,
  getMainTextVariant,
  getSecondaryTextVariant,
  getTextColor,
} from 'utils/accumulators';
import { getUnlimitedVariant } from 'utils/accumulators/getUnlimitedVariant';
import { getIsTariffAccumsFttbDivideEnabled } from 'utils';

import { UnlimitedAccumulatorTextContent } from '../UnlimitedAccumulatorTextContent/UnlimitedAccumulatorTextContent';
import { BaseTextContent } from '../BaseTextContent';
import { getViewVariant } from './utils';
import { GiftedAccumulator } from '../GiftedAccumulator';
import { UnlimitedForDay } from '../UnlimitedAccumulatorTextContent/UnlimitedForDay';
import {
  getTextColorForUnlimitedForDay,
  getTextForUnlimitedForDay,
} from '../UnlimitedAccumulatorTextContent/utils';

interface IProps {
  accumulator: EnrichedAccumItemType;
  isInline?: boolean;
  isMobile?: boolean;
  onMetricView?: (data: string) => void;
  services?: AccumServiceType;
  tetheringIsLow?: boolean;
  userType: string;
}

const getFormattedDate = (dateString: string) => {
  return DateTime.fromISO(dateString).toLocaleString({ day: 'numeric', month: 'numeric' });
};

export const ProstoySvayzZTariff: React.FC<IProps> = ({
  accumulator,
  isInline = false,
  services,
  tetheringIsLow,
  isMobile,
  userType,
  onMetricView,
}) => {
  const isMobileDimension = useIsMobileDimension();
  const isMobileSize = isMobile || isMobileDimension;
  const {
    rest,
    baseRest,
    displayUnit,
    isLow = false,
    isUnlimited,
    size,
    resetDateTimeUtc,
    isGiftBucket,
    resetDate,
  } = accumulator;
  const unit = formatTimeUnit(displayUnit?.toLowerCase() ?? '');
  const viewVariant = getViewVariant({ rest, baseRest, isUnlimited, size, isGifted: isGiftBucket });
  const isTariffDivideEnabled = getIsTariffAccumsFttbDivideEnabled();
  const resetAccumDate = getIsTariffAccumsFttbDivideEnabled() ? resetDateTimeUtc : resetDate;
  const isStandardUnlimited = !isGiftBucket && !!isUnlimited;
  const unlimitedAccumulatorVariant = getUnlimitedVariant({
    accumulator,
    services,
    tetheringIsLow,
  });

  return (
    <>
      <UnlimitedAccumulatorTextContent
        mainText={unit}
        mainTextVariant={getMainTextVariant(isInline, isMobileSize)}
        onMetricView={onMetricView}
        secondaryText={resetAccumDate ? `до ${getFormattedDate(resetAccumDate)}` : ''}
        secondaryTextColor="secondary"
        secondaryTextVariant={getSecondaryTextVariant(isMobileSize)}
        shouldRender={
          isStandardUnlimited &&
          (isTariffDivideEnabled ? unlimitedAccumulatorVariant === 'UnlimitedBase' : true)
        }
        userType={userType}
      />
      <UnlimitedForDay
        onMetricView={onMetricView}
        shouldRender={
          isStandardUnlimited &&
          isTariffDivideEnabled &&
          unlimitedAccumulatorVariant === 'Unlimited24Hours'
        }
        text={getTextForUnlimitedForDay(services?.unlimited24?.expirationDate)}
        textColor={getTextColorForUnlimitedForDay(services?.unlimited24?.expirationDate)}
        textVariant={getMainTextVariant(false, isMobileSize)}
        userType={userType}
      />
      <BaseTextContent
        mainText={`${baseRest} ₽ за 1 ${unit}`}
        mainTextColor="primary"
        mainTextVariant={getMainTextVariant(isInline, isMobileSize)}
        shouldRender={viewVariant === 'pricePerUnit'}
      />
      <BaseTextContent
        mainText={`${rest} ${unit}`}
        mainTextColor={getTextColor(isLow)}
        mainTextVariant={getMainTextVariant(isInline, isMobileSize)}
        secondaryText={`из ${size}`}
        secondaryTextColor="primary"
        secondaryTextVariant={getSecondaryTextVariant(isMobileSize)}
        shouldRender={viewVariant === 'rest-and-size'}
      />
      <BaseTextContent
        mainText={`${rest} ${unit}`}
        mainTextColor={getTextColor(isLow)}
        mainTextVariant={getMainTextVariant(isInline, isMobileSize)}
        secondaryText={resetAccumDate ? `до ${getFormattedDate(resetAccumDate)}` : undefined}
        secondaryTextColor="secondary"
        secondaryTextVariant={getSecondaryTextVariant(isMobileSize)}
        shouldRender={viewVariant === 'rest-only'}
      />
      <GiftedAccumulator
        accumulator={accumulator}
        isInline={isInline}
        isMobile={isMobileSize}
        shouldRender={viewVariant === 'gifted'}
      />
    </>
  );
};
